import { useState, useEffect, useCallback, MutableRefObject } from 'react'
import { $fetch, emitEvent } from '../utils'
import { getTokens } from '../utils/requests'
import { setFingerprintEnabled } from '../utils/fingerprint.ts'
import { URL_BASE } from '../common.ts'
import { AllowedDomain, Config } from './config'
import { createConfig } from './createConfig.ts'
import type { Environment } from '../common.ts'
import { DOMAIN_CONFIG, CURRENT_DOMAIN } from './../domains'


import type {
  ApplicationInterface,
  InformationInterface,
  LanguageType,
  OrganizationsInterface,
  TranslationsInterface
} from './auth.d.ts'

export type useAuthProps = {
  token?: string,
  env?: Environment,
  ref?: MutableRefObject<HTMLDivElement | null>,
  state?: string,
  onSessionBlocked?: (blocked: boolean) => void
}

export type useAuthFail = {
  code: number,
  message: string,
  details: string,
} | null | undefined

export type userInfoData = {
  language: LanguageType,
  hasProfile: boolean,
  hasPharmacyProfile: boolean,
  showLocker: boolean,
  isAdminUser: boolean,
  loaded: boolean,
  current_delegation: string,
  applications: ApplicationInterface[],
  organizations: OrganizationsInterface,
  information: InformationInterface,
  translations: TranslationsInterface,
  maxFreeText: number[],
  colors: {
    primary?: string
    secondary?: string
  },
  user_uuid: string
}

const DEFAULT_USER_INFO: userInfoData = {
  language: 'es',
  hasPharmacyProfile: false,
  hasProfile: true,
  showLocker: false,
  isAdminUser: false,
  loaded: false,
  current_delegation: '',
  applications: [],
  organizations: {},
  information: {},
  translations: {},
  maxFreeText: [30, 30],
  colors: {},
  user_uuid: ''
}

let checkTokenInterval: ReturnType<typeof setTimeout>

export function useAuth ({
  token,
  env = 'dev',
  ref,
  state,
  onSessionBlocked
} : useAuthProps = {}) {

  
  function sendError (payload: useAuthFail) {
    console.error(payload)
    setFail(payload)
    return payload
  }
  
  const [config, setConfig] = useState<Config>({} as Config)
  const [userInfo, setUserInfo] = useState<userInfoData>(DEFAULT_USER_INFO)
  const [userToken, setToken] = useState<string | null | undefined>(token)
  const [pending, setPending] = useState<boolean>(false)
  const [fail, setFail] = useState<useAuthFail>()
  const [domain, setDomain] = useState<AllowedDomain>('')

  useEffect(() => {
    setUserInfo(DEFAULT_USER_INFO)
  }, [])

  useEffect(() => {
    setToken(token)
  }, [token])

  useEffect(() => {
    getUserInfo()
  }, [userToken])

  // LOGOUT

  useEffect(() => {
    document.removeEventListener('visibilitychange', checkToken)
    document.addEventListener('visibilitychange', checkToken)
    checkToken()
    return () => {
      document.removeEventListener('visibilitychange', checkToken)
    }
  }, [userToken, config])

  async function logout () {
    try {
      setFail(null)
      setPending(true)
      const url = config.urls?.url_logout as string
      if (!url || !userToken) return

      await $fetch({
        method: 'POST',
        url: `${url}?token=${userToken}`,
        language: userInfo.language
        // state
      })
      getTokens.clear()
      setToken(null)
      emitEvent(ref?.current, 'logged-out', {domain})
    } catch (error: any) {
      return sendError({
        code: 500,
        message: 'Ha habido un error inesperado',
        details: String((error?.message) || error || '')
      })
    } finally {
      setPending(false)
    }
  }

  useEffect(() => {
    window.addEventListener('onLogout', logout)
    return () => {
      window.removeEventListener('onLogout', logout)
    }
  }, [userToken, config])

  // USER INFO

  async function getUserInfo (language?: LanguageType) {
    try {
      setFail(null)
      setPending(true)
      let domain = CURRENT_DOMAIN
      const domainConfig = DOMAIN_CONFIG[domain]

      if (!userToken) return

      // fetch json config
      const fileName = `topbar-config-${env}.json?${new Date().getTime() + 3000}`
      const res = await fetch(URL_BASE + fileName)
      const dataConfig = await res.json()
      setFingerprintEnabled(dataConfig['fingerprint-enabled'])

      const urlTopBarInfo = domainConfig.GATEWAY_BASE_URL + `/profile/v1/topbar-information?token=${userToken}`

      // fetch topbar info
      const data = await $fetch({
        url: urlTopBarInfo,
        language: language || 'es',
        state
      })
      
      if (data.code === 401) {
        setToken(null)
        emitEvent(ref?.current, 'logged-out', { domain: null })
        return sendError({
          code: 401,
          message: 'Unauthorized',
          details: ''
        })
      }
      
      emitEvent(ref?.current, 'topbar-information', data)
      language = language || (data.customization_info?.data?.language ?? 'es')
      domain = data.domain || domain
      const config = createConfig(dataConfig, env, domain)
      setDomain(domain)
      setConfig(config)

      setUserInfo({
        loaded: true,
        hasProfile: !!data.has_profile,
        hasPharmacyProfile: !!data.pharmacy_profile,
        showLocker: !!data.switch_user,
        isAdminUser: !!data.organization_admin,
        language: language as LanguageType,
        current_delegation: data.current_delegation || '',
        applications: (data?.applications?.data) || [],
        organizations: (data.parent_cooperative_organizations?.data) || [],
        information: (data.customization_info?.data) || {},
        translations: (config.translations?.[language as LanguageType]) || {},
        maxFreeText: [
          (data.max_free_text_1_length) || 30,
          (data.max_free_text_2_length) || 30
        ],
        colors: {
          primary: data.customization_info?.data?.colors?.primary_color?.hex_color,
          secondary: data.customization_info?.data?.colors?.secondary_color?.hex_color
        },
        user_uuid: data.user_uuid
      })
      emitEvent(ref?.current, 'logged-in')
      return data
    } catch (error: any) {
      return sendError({
        code: 500,
        message: 'Ha habido un error inesperado',
        details: String((error?.message) || error || '')
      })
    } finally {
      setPending(false)
    }
  }

  // Check token

  function resetCheckTimeout () {
    clearTimeout(checkTokenInterval)
    checkTokenInterval = setTimeout(() => checkToken(), config.polling_timeout_ms || 10000)
  }

  async function checkToken () {
    try {
      setFail(null)
      setPending(true)
      clearTimeout(checkTokenInterval)
      const url = config.urls?.url_checktoken as string
      if (document.hidden || !url || !userToken) return

      const data = await $fetch({
        url: `${url}?token=${userToken}`,
        language: userInfo.language,
        state
      })
      if (data.code === 401) {
        getTokens.clear()
        setToken(null)
        emitEvent(ref?.current, 'logged-out', {domain})
        return sendError({
          code: 401,
          message: 'Unauthorized',
          details: ''
        })
      }
      // Check if session is changed
      if (data.jti && data.jti !== userToken) setToken(data.jti)
      // Check if session is blocked
      onSessionBlocked && onSessionBlocked(data.session_blocked)
      return { ...data, code: 200 }
    } catch (error: any) {
      return sendError({
        code: 500,
        message: 'Ha habido un error inesperado',
        details: String((error?.message) || error || '')
      })
    } finally {
      setPending(false)
      resetCheckTimeout()
    }
  }

  // Change language

  const changeLanguage = useCallback((event: Event & { detail?: LanguageType }) => {
    if (event?.detail !== userInfo.language) {
      getUserInfo(event?.detail)
    }
  }, [userInfo])

  useEffect(() => {
    window.addEventListener('change-lang', changeLanguage)
    return () => {
      window.removeEventListener('change-lang', changeLanguage)
    }
  }, [changeLanguage])

  return {
    userToken,
    pending,
    fail,
    config,
    userInfo,
    getUserInfo,
    checkToken,
    resetCheckTimeout,
    setToken,
    logout,
    domain
  }
}